import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  useUpdateFormMutation,
  useUpdateFormLocationStatusMutation,
} from '../../store/api/localApi';
import { FormStatus, FormDefinition, Location } from '../../types';
import { MapPinIcon } from '@heroicons/react/24/outline';

export interface PublishToLocationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  form: FormDefinition;
  locations: Location[];
}

const PublishToLocationsModal: React.FC<PublishToLocationsModalProps> = ({
  isOpen,
  onClose,
  form,
  locations,
}) => {
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>(form.locationIds || []);
  const [locationStatuses, setLocationStatuses] = useState<Record<string, FormStatus>>(
    form.locationStatuses || {}
  );
  const [updateForm, { isLoading: isSaving }] = useUpdateFormMutation();
  const [updateLocationStatus] = useUpdateFormLocationStatusMutation();

  const allSelected = locations.length > 0 && selectedLocationIds.length === locations.length;

  const toggleLocation = (locationId: string) => {
    setSelectedLocationIds((prev) => {
      const next = prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId];
      // If adding a location, default its status to DRAFT
      if (!prev.includes(locationId) && !locationStatuses[locationId]) {
        setLocationStatuses((s) => ({ ...s, [locationId]: FormStatus.DRAFT }));
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedLocationIds([]);
    } else {
      const allIds = locations.map((l) => l.id);
      setSelectedLocationIds(allIds);
      // Set defaults for any locations not yet in statuses
      const newStatuses = { ...locationStatuses };
      for (const loc of locations) {
        if (!newStatuses[loc.id]) {
          newStatuses[loc.id] = FormStatus.DRAFT;
        }
      }
      setLocationStatuses(newStatuses);
    }
  };

  const toggleLocationStatus = (locationId: string) => {
    setLocationStatuses((prev) => ({
      ...prev,
      [locationId]: prev[locationId] === FormStatus.ACTIVE ? FormStatus.DRAFT : FormStatus.ACTIVE,
    }));
  };

  const handleSave = async () => {
    try {
      // Save location assignments
      await updateForm({
        formId: form.id,
        updates: { locationIds: selectedLocationIds },
      }).unwrap();

      // Update per-location statuses for selected locations
      const statusPromises = selectedLocationIds.map((locId) => {
        const status = locationStatuses[locId] || FormStatus.DRAFT;
        return updateLocationStatus({
          formId: form.id,
          locationId: locId,
          status,
        }).unwrap();
      });
      await Promise.all(statusPromises);

      onClose();
    } catch {
      // Error handled by RTK Query
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose} data-testid="publish-modal">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                  Publish to Locations
                </Dialog.Title>
                <p className="mt-1 text-sm text-gray-500">
                  Select which locations should have access to &ldquo;{form.title}&rdquo; and set
                  their status.
                </p>

                <div className="mt-4 space-y-2">
                  {/* Select All */}
                  <label className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50 cursor-pointer border-b border-gray-200 pb-3 mb-1">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-900">Select All</span>
                  </label>

                  {/* Location list */}
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    {locations.map((location) => {
                      const isSelected = selectedLocationIds.includes(location.id);
                      const locStatus = locationStatuses[location.id] || FormStatus.DRAFT;
                      const isActive = locStatus === FormStatus.ACTIVE;

                      return (
                        <div
                          key={location.id}
                          data-testid={`publish-location-row-${location.id}`}
                          className="flex items-center justify-between p-2 rounded hover:bg-gray-50"
                        >
                          <label className="flex items-center space-x-3 cursor-pointer flex-1 min-w-0">
                            <input
                              type="checkbox"
                              data-testid={`publish-location-checkbox-${location.id}`}
                              checked={isSelected}
                              onChange={() => toggleLocation(location.id)}
                              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                            <div className="flex items-center space-x-2 min-w-0">
                              <MapPinIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span className="text-sm text-gray-700 truncate">
                                {location.name}
                              </span>
                            </div>
                          </label>
                          {isSelected && (
                            <button
                              type="button"
                              data-testid={`publish-status-toggle-${location.id}`}
                              onClick={() => toggleLocationStatus(location.id)}
                              className={`ml-2 flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                                isActive
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                  : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                              }`}
                            >
                              {isActive ? 'Active' : 'Draft'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    data-testid="publish-modal-cancel"
                    onClick={onClose}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    data-testid="publish-modal-save"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PublishToLocationsModal;
